import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Plus, Save } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import type { Equipo } from "@/lib/equipos";
import { TIPO_FUENTE_LABEL } from "@/lib/fuentes";

// Espejo de la validación de servidor (factory/fuente_dto.go): nombre obligatorio ≤120,
// tipo del dominio, descripción ≤500, equipo obligatorio. id_equipo se coacciona a número
// porque el <select> nativo entrega string.
const fuenteSchema = z.object({
  id_equipo: z.coerce.number().int().positive("Selecciona un equipo"),
  nombre_fuente: z
    .string()
    .trim()
    .min(1, "El nombre es obligatorio")
    .max(120, "Máximo 120 caracteres"),
  tipo: z.enum(["ELECTRICA", "NEUMATICA", "HIDRAULICA", "MECANICA", "TERMICA", "QUIMICA", "OTRA"]),
  descripcion: z.string().trim().max(500, "Máximo 500 caracteres"),
  activo: z.boolean(),
});

// El esquema coacciona id_equipo (z.coerce.number): el tipo de entrada difiere del de
// salida ya validado. Con @hookform/resolvers v5 hay que tipar useForm con ambos.
type FuenteFormInput = z.input<typeof fuenteSchema>;
export type FuenteFormValues = z.output<typeof fuenteSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<FuenteFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "VALIDACION" && err.fields) {
      let mapped = false;
      for (const campo of ["nombre_fuente", "tipo", "descripcion", "id_equipo"] as const) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    setFormError(err.message || "No se pudo guardar la fuente.");
    return;
  }
  setFormError("No se pudo guardar la fuente. Comprueba tu conexión.");
}

// TIPOS son los valores del <select> de tipo de energía, en orden de presentación.
const TIPOS = [
  "ELECTRICA",
  "NEUMATICA",
  "HIDRAULICA",
  "MECANICA",
  "TERMICA",
  "QUIMICA",
  "OTRA",
] as const;

/**
 * FuenteForm es el formulario reutilizable de alta/edición de fuentes de energía
 * (react-hook-form + zod). En edición el equipo queda fijo (no se reparenta) y el código
 * FE-XXXX se muestra de solo lectura. En alta ofrece «Guardar y añadir otra al mismo
 * equipo» (chained-add): mantiene el equipo seleccionado y limpia nombre/descripción.
 */
export function FuenteForm({
  defaultValues,
  equipos,
  codigo,
  submitLabel,
  showAddAnother,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: FuenteFormValues;
  equipos: Equipo[];
  codigo?: string;
  submitLabel: string;
  showAddAnother: boolean;
  pending: boolean;
  onSubmit: (values: FuenteFormValues, addAnother: boolean) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const isEdit = codigo !== undefined;
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<FuenteFormInput, unknown, FuenteFormValues>({
    resolver: zodResolver(fuenteSchema),
    defaultValues,
  });

  function run(addAnother: boolean) {
    return handleSubmit(async (values) => {
      setFormError(null);
      try {
        await onSubmit(values, addAnother);
        if (addAnother) {
          // Encadena otra alta al MISMO equipo: conserva id_equipo y tipo, limpia el resto.
          reset({ ...values, nombre_fuente: "", descripcion: "" });
        }
      } catch (err) {
        aplicarError(err, setError, setFormError);
      }
    });
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={run(false)} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      {codigo ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="codigo_fuente">Código</Label>
          <Input id="codigo_fuente" value={codigo} disabled readOnly />
          <p className="text-slate-500 text-sm">El código lo asigna el sistema y no cambia.</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_equipo">Equipo</Label>
        <Select
          id="id_equipo"
          disabled={isEdit}
          aria-invalid={errors.id_equipo ? true : undefined}
          {...register("id_equipo")}
        >
          <option value="0" disabled>
            Selecciona un equipo
          </option>
          {equipos.map((equipo) => (
            <option key={equipo.id} value={equipo.id}>
              {equipo.codigo_equipo} · {equipo.nombre_equipo}
            </option>
          ))}
        </Select>
        {isEdit ? (
          <p className="text-slate-500 text-sm">El equipo de una fuente no se puede cambiar.</p>
        ) : null}
        <FieldError>{errors.id_equipo?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="nombre_fuente">Nombre</Label>
        <Input
          id="nombre_fuente"
          autoComplete="off"
          aria-invalid={errors.nombre_fuente ? true : undefined}
          {...register("nombre_fuente")}
        />
        <FieldError>{errors.nombre_fuente?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tipo">Tipo de energía</Label>
        <Select id="tipo" aria-invalid={errors.tipo ? true : undefined} {...register("tipo")}>
          {TIPOS.map((tipo) => (
            <option key={tipo} value={tipo}>
              {TIPO_FUENTE_LABEL[tipo]}
            </option>
          ))}
        </Select>
        <FieldError>{errors.tipo?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descripcion">Descripción (opcional)</Label>
        <Textarea
          id="descripcion"
          aria-invalid={errors.descripcion ? true : undefined}
          {...register("descripcion")}
        />
        <FieldError>{errors.descripcion?.message}</FieldError>
      </div>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("activo")}
        />
        <span className="text-base text-slate-800">Activa</span>
      </label>

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        {showAddAnother ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => void run(true)()}
            disabled={pending}
          >
            <Plus className="h-5 w-5" aria-hidden="true" />
            Guardar y añadir otra
          </Button>
        ) : null}
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
